import { useQuery } from '@tanstack/react-query';

export interface FeatureFlags {
  newEventManagementEnabled: boolean;
  newApplicationEnabled: boolean;
  newIntegrationEnabled: boolean;
  aiInterviewEnabled: boolean;
  aiStudioEnabled: boolean;
  aiSummariesEnabled: boolean;
  candidateSourcingEnabled: boolean;
  appServicesEnabled: boolean;
  csvExportEnabled: boolean;
  linkedinProfileUrlEnabled: boolean;
  newSeekIntegrationEnabled: boolean;
  newApplyWithSeekEnabled: boolean;
  jobTemplateEnabled: boolean;
  videoConferenceIntegrationEnabled: boolean;
  // Add other feature flags here as needed
}

interface FeatureFlagsResponse {
  flags: FeatureFlags;
}

// API function to fetch feature flags
async function fetchFeatureFlags(): Promise<FeatureFlags> {
  const response = await fetch('/api/v4/feature_flags', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    credentials: 'same-origin'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch feature flags');
  }

  const data: FeatureFlagsResponse = await response.json();
  return data.flags;
}

// Cache key for React Query
const FEATURE_FLAGS_QUERY_KEY = ['feature-flags'];

// Custom hook for feature flags with efficient caching
export function useFeatureFlags() {
  return useQuery({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: fetchFeatureFlags,
    staleTime: 10 * 60 * 1000, // 10 minutes - feature flags don't change often
    cacheTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: true, // Refetch when user returns to tab (most likely time for changes)
    refetchOnMount: false, // Don't refetch on mount if we have cached data
    retry: 2
  });
}

// Individual hook for specific flags (for better DX)
export function useFeatureFlag<K extends keyof FeatureFlags>(flag: K): boolean {
  const { data: flags, isLoading, error } = useFeatureFlags();

  if (isLoading) return false; // Default to disabled while loading
  if (error) return false; // Default to disabled on error

  return flags?.[flag] ?? false;
}
